/**
 * very simple Responsive image polyfill - handles default <f:responsiveImage /> implementation
 *
 * this implementation will render the following markup
 *
 * <noscript data-responsive-image="true">
 *   <picture>
 *     <source width="480px" height="360px" src="path/to/small.jpg" />
 *     <source data-breakpoint="medium" width="640px" height="300px" src="path/to/medium-source.jpg" />
 *     <img width="640px" height="300px" src="path/to/large.jpg" loading="lazy" />
 *   </picture>
 * </noscript>
 *
 * we will only add 2 things to browser-standards:
 * 1) named media queries (data-breakpoint)
 * 2) optional (if the browser does not support this already) width/height handling for the img tag
 *    - the width/height attributes will be set on the img element - according to the matched source
 *
 * this is very limited and needs some rules to be obeyed in order to work correctly:
 *
 * - only picture/source syntax supported
 * - sources need to be rendered in reverse mobile-first ordering
 *
 */
(function (context, namedBreakpoints) {

    const addPolyfill = document.createElement('source')['width'] === undefined;

    function changeWidthAndHeight(picture) {
        const img = picture.querySelector('img');
        let width = img.dataset.originalWidth;
        let height = img.dataset.originalHeight;
        for (let source of picture.querySelectorAll('source')) {
            if (matchMedia(source.media).matches) {
                if (source.getAttribute('width')) {
                    width = source.getAttribute('width');
                }
                if (source.getAttribute('height')) {
                    height = source.getAttribute('height');
                }
                break;
            }
        }
        img.width = width;
        img.height = height;
    }

    const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(entry => {
            changeWidthAndHeight(entry.target);
        })
    });

    // @see https://ticket.pixelpoint.at/issues/33240
    // regular browsers do it with innerHTML but safari on iOS is a different story
    let accessor;
    const testNode = document.createElement('div');
    testNode.innerHTML = '<noscript><span></span></noscript>';
    const assertionNode = testNode.querySelector('noscript');
    if (assertionNode.innerHTML.indexOf('<') !== -1) {
        accessor = node => node.innerHTML;
        accessProperty = 'innerHTML';
    } else if (assertionNode.innerText.indexOf('<') !== -1) {
        accessor = node => node.innerText;
    } else if (assertionNode.textContent.indexOf('<') !== -1) {
        accessor = node => node.textContent;
    }
    /**
     * @param {HTMLElement} noscript
     * @return {?HTMLPictureElement}
     */
    function getPictureFromNoscriptElement(noscript) {
        return new DOMParser().parseFromString(accessor(noscript), 'text/html').querySelector('picture');
    }

    function processNodes(context) {
        Array.from(context.querySelectorAll('noscript[data-responsive-image]')).forEach(noscript => {
            const picture = getPictureFromNoscriptElement(noscript);
            Array.from(picture.querySelectorAll('[data-breakpoint]')).forEach(source => {
                const namedBreakpoint = namedBreakpoints[source.dataset.breakpoint];
                if (namedBreakpoint && namedBreakpoint.query !== "all") {
                    source.media = namedBreakpoint.query || `(min-width: ${source.dataset.breakpoint}px)`;
                }
            });
            if (addPolyfill) {
                const img = picture.querySelector('img');
                img.dataset.originalWidth = `${img.width || img.naturalWidth || ''}`;
                img.dataset.originalHeight = `${img.height || img.naturalHeight || ''}`;
                changeWidthAndHeight(picture);
                resizeObserver.observe(picture);
            }
            noscript.parentNode.replaceChild(picture, noscript);
        });
    }

    const mutations = new MutationObserver(() => {
        processNodes(context);
    });
    processNodes(context);
    mutations.observe(context, {
        subtree: true,
        childList: true,
    });

})(document, window['namedBreakpoints'] ?? {});
